import { useApolloClient } from '@apollo/client';
import { JupiterTheme } from '@jupiterone/jupiter-web-toolkit/src/theme/types';
import CircularProgress from '@material-ui/core/CircularProgress';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import { createStyles, makeStyles } from '@material-ui/styles';
import React from 'react';
import HideIcon from 'react-feather/dist/icons/eye-off';
import InfoIcon from 'react-feather/dist/icons/info';
import ListIcon from 'react-feather/dist/icons/list';
import MaxIcon from 'react-feather/dist/icons/maximize-2';
import MinIcon from 'react-feather/dist/icons/minimize-2';
import MoreIcon from 'react-feather/dist/icons/more-horizontal';
import { useBooleanState } from 'react-use-object-state';
import { FETCH_NEIGHBORS_QUERY } from '../../queries/queries';
import PipeIcon from '../icons/pipe';
import { Pack } from '../reducer/packVertices';
import { GraphData, GraphEdge, GraphVertex, SelectedNode } from '../types';
import { getPackId } from '../utils/packUtils';
import PackContextMenu from './PackContextMenu';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    packMenu: {
      position: 'absolute',
      left: 100,
      width: 250,
    },
    root: {
      display: 'inline-flex',
      flexDirection: 'row',
      alignItems: 'center',
      background: theme.palette.primary.main,
      borderRadius: 4,
      padding: theme.spacing(1),
    },
    menuLine: {
      color: theme.palette.primary.main,
      strokeOpacity: 0.7,
      strokeWidth: 1,
      margin: '0 3px',
      height: 22,
    },
    menuButton: {
      tabIndex: 0,
      cursor: 'pointer',
      color: theme.palette.text.contrast,
      width: 26,
      height: 26,
      borderRadius: 0,
      '&:focus': {
        color: theme.palette.grey[200],
      },
    },
    menuText: {
      color: theme.palette.text.contrast,
    },
    spinner: {
      marginRight: 3,
      marginLeft: 3,
      color: theme.palette.primary.main,
    },
    menuContainer: {
      position: 'relative',
    },
    dropDownMenuPaper: {
      position: 'absolute',
      top: 0,
      right: theme.spacing(-8.5),
    },
  })
);

interface Props {
  onHideNode: (id: string) => void;
  onHideUnrelated: (id: string) => void;
  onTogglePack: (id: string) => void;
  onInspect: (id: string) => void;
  onLoadMore: (
    loadedId: string,
    newData: { vertices: GraphVertex[]; edges: GraphEdge[] }
  ) => void;
  selectedNode: SelectedNode;
  inspectedNodeId: string | null;
  packs: Pack[];
  nodeIdsThatHaveFetchedNeighbors: string[];
  graphData: GraphData;
}

const EntityContextMenu = ({
  onHideUnrelated,
  onHideNode,
  onTogglePack,
  onInspect,
  onLoadMore,
  selectedNode,
  packs,
  nodeIdsThatHaveFetchedNeighbors,
  graphData,
  inspectedNodeId,
}: Props) => {
  const client = useApolloClient();
  const classes = useStyles();
  const loading = useBooleanState(false);
  const packMenuVisible = useBooleanState(false);

  const visibilityDropDownOpen = useBooleanState(false);

  if (!selectedNode.id) {
    return null;
  }

  const handleHideUnrelated = () => {
    visibilityDropDownOpen.setFalse();
    onHideUnrelated(selectedNode.id as string);
  };

  const handleHideNode = () => {
    visibilityDropDownOpen.setFalse();
    onHideNode(selectedNode.id as string);
  };

  const loadMore = async () => {
    loading.setTrue();
    try {
      const { data } = await client.query({
        query: FETCH_NEIGHBORS_QUERY,
        variables: { id: selectedNode.id, depth: 1 },
      });

      const neighbors: GraphData = data.vertex.neighbors;
      onLoadMore(selectedNode.id as string, {
        vertices:
          neighbors.vertices.filter(
            (v: GraphVertex) => v.id !== selectedNode.id
          ) || [],
        edges: data.vertex.edges || [],
      });
    } finally {
      loading.setFalse();
    }
  };
  const style = {
    position: 'absolute' as any,
    left: selectedNode.position[0],
    top: selectedNode.position[1],
    transform: 'translateX(-50%) translateY(-56px)',
  };

  const selectedPack: Pack | undefined = packs.find(
    (p) => getPackId(p) === selectedNode.id
  );
  const containingPack: Pack | undefined = packs.find(
    (p) => p.packedVerticesIds.indexOf(selectedNode.id || '') >= 0
  );

  const canLoadMore =
    !selectedPack &&
    nodeIdsThatHaveFetchedNeighbors.indexOf(selectedNode.id) === -1;

  return (
    <div style={style}>
      <nav className={classes.root}>
        <>
          <InfoIcon
            className={classes.menuButton}
            aria-label="Inspect"
            onClick={() => onInspect(selectedNode.id as string)}
          />

          <PipeIcon className={classes.menuLine} />
        </>

        {canLoadMore && (
          <>
            {!loading.state ? (
              <MoreIcon
                data-testid="load-more"
                className={classes.menuButton}
                aria-label="Load neighbors"
                onClick={loadMore}
              />
            ) : (
              <CircularProgress
                size={20}
                thickness={5}
                className={classes.spinner}
                color="inherit"
              />
            )}
            <PipeIcon className={classes.menuLine} />
          </>
        )}

        {selectedPack && (
          <>
            <MaxIcon
              data-testid={'expand'}
              className={classes.menuButton}
              aria-label="Expand"
              onClick={() => onTogglePack(getPackId(selectedPack))}
            />

            <PipeIcon className={classes.menuLine} />

            <ListIcon
              data-testid={'list'}
              className={classes.menuButton}
              aria-label="List child nodes"
              onClick={packMenuVisible.toggle}
            />
            <Typography variant="body1" className={classes.menuText}>
              {selectedPack.packedVerticesIds.length}
            </Typography>

            {!selectedPack && <PipeIcon className={classes.menuLine} />}
          </>
        )}

        {containingPack && (
          <>
            <MinIcon
              data-testid={'collapse'}
              className={classes.menuButton}
              aria-label="Collapse"
              onClick={() => onTogglePack(getPackId(containingPack))}
            />

            <PipeIcon className={classes.menuLine} />
          </>
        )}

        {!selectedPack && (
          <HideIcon
            className={classes.menuButton}
            aria-label="Hide"
            onClick={visibilityDropDownOpen.toggle}
          />
        )}
      </nav>
      {visibilityDropDownOpen.state && (
        <div className={classes.menuContainer}>
          <Paper className={classes.dropDownMenuPaper}>
            <MenuList>
              <MenuItem>
                <Typography variant="caption" onClick={handleHideNode}>
                  Hide This Node
                </Typography>
              </MenuItem>
              <MenuItem>
                <Typography variant="caption" onClick={handleHideUnrelated}>
                  Hide Unrelated Nodes
                </Typography>
              </MenuItem>
            </MenuList>
          </Paper>
        </div>
      )}
      <div className={classes.packMenu}>
        <PackContextMenu
          pack={selectedPack}
          open={packMenuVisible.state}
          graphData={graphData}
          onSelectNode={onInspect}
          inspectedNodeId={inspectedNodeId}
        />
      </div>
    </div>
  );
};

export default EntityContextMenu;
